import type { Lead } from '@/types';

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RiUserLine,
  RiEyeLine,
  RiAddLine,
  RiExternalLinkLine,
  RiStickyNoteLine,
  RiChat3Line,
  RiDownloadLine,
  RiContactsLine
} from '@remixicon/react';
import { Button, Chip, Tooltip, Select, SelectItem } from '@heroui/react';
import type { SortDescriptor } from '@heroui/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { toast } from 'react-toastify';

import { TablePage, TableFilters, ColumnsToggleDropdown, FilterSelect, FilterMultiSelect } from '@/components/table';
import {
  leadsListParamsAtom,
  leadsQueryAtom,
  assignLeadToSelf,
  assignLeadOwner,
  updateLeadStatus,
  batchUpdateLeads
} from '@/store';
import { profileAtom } from '@/store/auth';
import { PermissionGuard } from '@/components/shared/PermissionGuard';
import { ConfirmDeleteModal } from '@/components/shared/ConfirmDeleteModal';
import { SearchInput } from '@/components/shared/SearchInput';
import { DateTimeDisplay } from '@/components/shared/DateTimeDisplay';
import { LeadScore } from '@/components/leads/LeadScore';

// Custom hooks
import {
  useLeadsFilters,
  useLeadAssignOwner,
  useLeadAssignSelf,
  useLeadStatusChange,
  useLeadNotes,
  useLeadInteractions,
  useLeadContacts
} from '@/hooks/leads';
import { useVisibleColumns } from '@/hooks/table';
import { useTableExport } from '@/components/table/useTableExport';
import { ExportConfirmModal } from '@/components/table/ExportConfirmModal';

// Components
import { AssignOwnerModal } from '@/components/leads/AssignOwnerModal';
import { AssignSelfConfirmModal } from '@/components/leads/AssignSelfConfirmModal';
import { AddNoteModal } from '@/components/leads/AddNoteModal';
import { AddInteractionModal } from '@/components/leads/AddInteractionModal';
import { AddContactModal } from '@/components/leads/AddContactModal';
import { AdvancedFiltersPanel } from '@/components/leads/AdvancedFiltersPanel';

export default function LeadsPage() {
  const { data, isLoading, isError, error } = useAtomValue(leadsQueryAtom);
  const [params, setParams] = useAtom(leadsListParamsAtom);
  const navigate = useNavigate();

  const leads = React.useMemo<Lead[]>(() => data?.data ?? [], [data]);

  // Custom hooks
  const {
    searchFields,
    setSearchFields,
    localDomainSearch,
    setLocalDomainSearch,
    applyFilters,
    clearFilters,
    handlePageChange,
    handleDomainSearch,
    handleClearDomainSearch,
    handleStatusFilterChange,
    handleCompetitorsFilterChange,
    handleScoreFilterChange,
    handleOwnerFilterChange,
    handleEcommerceFilterChange,
    handleShopFilterChange,
    handleCollectedDateChange,
    handleSyncedDateChange,
    handleCreatedDateChange,
    handleUpdatedDateChange,
    currentStatus,
    currentCompetitors,
    currentScore,
    currentOwner,
    currentEcommerce,
    currentShop,
    currentCollectedDate,
    currentSyncedDate,
    currentCreatedDate,
    currentUpdatedDate,
    hasAnyFilter,
    activeFiltersCount,
    sortParam,
    setSortParam
  } = useLeadsFilters();

  const [sortDescriptor, setSortDescriptor] = React.useState<SortDescriptor | undefined>(undefined);

  React.useEffect(() => {
    if (!sortParam) {
      setSortDescriptor(undefined);
      return;
    }
    const firstSort = sortParam.split(',')[0];
    if (!firstSort) {
      setSortDescriptor(undefined);
      return;
    }
    const direction = firstSort.startsWith('-') ? 'descending' : 'ascending';
    const column = firstSort.replace(/^-/, '');
    setSortDescriptor((prev) => {
      if (prev?.column === column && prev?.direction === direction) {
        return prev;
      }
      return { column, direction };
    });
  }, [sortParam]);
  const { isModalOpen, selectedLead, isSubmitting, openModal, handleAssign, onModalOpenChange } = useLeadAssignOwner();
  const {
    isConfirmOpen: isAssignSelfOpen,
    selectedLead: selectedLeadForSelf,
    isSubmitting: isAssigningSelf,
    openConfirm: openAssignSelf,
    onConfirm: handleAssignSelf,
    onOpenChange: onAssignSelfOpenChange
  } = useLeadAssignSelf();
  const {
    isAddModalOpen: isAddNoteModalOpen,
    selectedLead: selectedLeadForNote,
    isSubmitting: isSubmittingNote,
    openAddModal: openAddNoteModal,
    handleCreateNote,
    onAddModalOpenChange: onAddNoteModalOpenChange
  } = useLeadNotes();
  const {
    isAddModalOpen: isAddInteractionModalOpen,
    selectedLead: selectedLeadForInteraction,
    isSubmitting: isSubmittingInteraction,
    openAddModal: openAddInteractionModal,
    handleCreateInteraction,
    onAddModalOpenChange: onAddInteractionModalOpenChange
  } = useLeadInteractions();
  const {
    isAddModalOpen: isAddContactModalOpen,
    selectedLead: selectedLeadForContact,
    isSubmitting: isSubmittingContact,
    openAddModal: openAddContactModal,
    handleCreateContact,
    onAddModalOpenChange: onAddContactModalOpenChange
  } = useLeadContacts();
  const { isUpdating, handleStatusChange } = useLeadStatusChange();
  const profile = useAtomValue(profileAtom);
  const assignSelf = useSetAtom(assignLeadToSelf);
  const clearOwner = useSetAtom(assignLeadOwner);
  const updateStatus = useSetAtom(updateLeadStatus);
  const batchUpdate = useSetAtom(batchUpdateLeads);

  // Function to trigger data refresh
  const triggerRefresh = React.useCallback(() => {
    setParams((prev) => ({ ...prev, refresh: Date.now() }));
  }, [setParams]);

  // Handler for opening all new domains in new tab
  const handleOpenAllDomains = React.useCallback(async () => {
    if (!profile) {
      toast.error('You must be logged in to perform this action');
      return;
    }

    // Filter leads with status 'new'
    const newLeads = leads.filter((lead) => lead.status === 'new');

    if (newLeads.length === 0) {
      toast.info('No leads with status "new" found');
      return;
    }

    // Limit to 15 leads
    const leadsToOpen = newLeads.slice(0, 15);
    const remainingCount = newLeads.length - leadsToOpen.length;

    if (remainingCount > 0) {
      toast.warning(`Only showing first 15 leads. ${remainingCount} leads will not be shown.`);
    }

    // Update leads in background
    try {
      const leadIds = leadsToOpen.map((lead) => lead.id);
      await batchUpdate({
        leadIds,
        status: 'dispensable',
        assignToMe: true
      });
      toast.success(`Updated ${leadsToOpen.length} lead(s) status to Dispensable and assigned to you`);
    } catch (error) {
      console.error('Error updating leads:', error);
      toast.error('Failed to update some leads');
    }

    // Open viewer page in new tab with lead IDs as query params
    const leadIds = leadsToOpen.map((lead) => lead.id).join(',');
    const viewerUrl = `/relationships/leads/viewer?ids=${leadIds}`;
    window.open(viewerUrl, '_blank', 'noopener,noreferrer');
  }, [leads, profile, batchUpdate]);

  // Handler for external link click - updates status and assigns to current user (only for status 'new')
  const handleExternalLinkClick = React.useCallback(
    async (e: React.MouseEvent<HTMLAnchorElement>, lead: Lead) => {
      e.stopPropagation();
      e.preventDefault();

      // Only update status to dispensable and assign to current user if status is 'new'
      if (profile && lead.status === 'new') {
        try {
          // Update status and assign in parallel
          await Promise.all([
            updateStatus({ leadId: lead.id, status: 'dispensable' }),
            assignSelf({ leadId: lead.id })
          ]);
        } catch (error) {
          // Silently handle errors - still open the link
          console.error('Failed to update lead:', error);
        }
      }

      // Open the link
      window.open(`https://${lead.domain}`, '_blank', 'noopener,noreferrer');
    },
    [profile, updateStatus, assignSelf, batchUpdate]
  );

  const renderCell = React.useCallback(
    (lead: Lead, columnKey: React.Key) => {
      switch (columnKey) {
        case 'domain':
          return (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className="hover:text-primary text-default-900 cursor-pointer text-left font-medium transition-colors"
                  onClick={() => navigate(`/relationships/leads/${lead.id}`)}
                >
                  {lead.domain}
                </button>
                <Tooltip content="Opens in new tab" showArrow>
                  <a
                    href={`https://${lead.domain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-primary text-default-400 cursor-pointer transition-colors"
                    onClick={(e) => handleExternalLinkClick(e, lead)}
                  >
                    <RiExternalLinkLine className="h-3.5 w-3.5" />
                  </a>
                </Tooltip>
              </div>
              {lead.title && <p className="text-default-500 text-xs">{lead.title}</p>}
            </div>
          );
        case 'owner':
          return lead.owner ? (
            <button
              type="button"
              className="text-default-500 hover:text-primary text-sm underline-offset-2 transition-colors hover:underline"
              onClick={() => navigate(`/users?filter[email]=${encodeURIComponent(lead.owner?.email ?? '')}`)}
            >
              {`${lead.owner.first_name} ${lead.owner.last_name}`}
            </button>
          ) : (
            <PermissionGuard permission="Lead.assignSelf">
              <Button
                size="sm"
                variant="light"
                color="primary"
                className="h-7 min-w-0 px-2 text-xs"
                onPress={() => openAssignSelf(lead)}
              >
                Assign to me
              </Button>
            </PermissionGuard>
          );
        case 'score':
          return <LeadScore lead={lead} />;
        case 'competitor_count':
          // Color logic: 1 competitor → red, 2 competitors → orange, 3+ → green
          let countColor: 'success' | 'warning' | 'danger' = 'danger';
          if (lead.competitor_count >= 3) {
            countColor = 'success';
          } else if (lead.competitor_count === 2) {
            countColor = 'warning';
          } else {
            countColor = 'danger';
          }
          return (
            <div className="flex flex-col gap-1">
              <Chip color={countColor} size="sm" variant="flat">
                <span className="font-semibold">{lead.competitor_count}</span>
              </Chip>
              {lead.competitors.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {lead.competitors.slice(0, 3).map((competitor, idx) => (
                    <Chip key={idx} size="sm" variant="flat" color="primary" className="text-xs">
                      {competitor}
                    </Chip>
                  ))}
                  {lead.competitors.length > 3 && (
                    <Chip size="sm" variant="flat" className="text-xs">
                      +{lead.competitors.length - 3}
                    </Chip>
                  )}
                </div>
              )}
            </div>
          );
        case 'ecommerce':
          return lead.is_ecommerce ? (
            <div className="flex flex-col gap-1">
              <Chip color="success" size="sm" variant="flat">
                Yes
              </Chip>
              {lead.ecommerce_platform && (
                <span className="text-default-500 text-xs">{lead.ecommerce_platform.name}</span>
              )}
            </div>
          ) : (
            <span className="text-default-400 text-sm">-</span>
          );
        case 'status':
          const statusColors: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger'> = {
            new: 'default',
            contacted: 'primary',
            followup: 'warning',
            closed: 'success',
            dispensable: 'danger'
          };
          const statusLabel =
            lead.status === 'dispensable' ? 'Dispensable' : lead.status.charAt(0).toUpperCase() + lead.status.slice(1);
          return (
            <Chip color={statusColors[lead.status] || 'default'} size="sm" variant="flat">
              {statusLabel}
            </Chip>
          );
        case 'collected_at':
          return lead.collected_at ? (
            <DateTimeDisplay dateString={lead.collected_at} />
          ) : (
            <span className="text-default-400 text-sm">-</span>
          );
        case 'synced_at':
          return lead.synced_at ? (
            <DateTimeDisplay dateString={lead.synced_at} />
          ) : (
            <span className="text-default-400 text-sm">-</span>
          );
        case 'updated_at':
          return lead.updated_at ? (
            <DateTimeDisplay dateString={lead.updated_at} />
          ) : (
            <span className="text-default-400 text-sm">-</span>
          );
        case 'actions':
          return (
            <div className="relative flex items-center justify-end gap-2">
              <PermissionGuard permission="Lead.show">
                <Tooltip content="View Details">
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    onPress={() => navigate(`/relationships/leads/${lead.id}`)}
                  >
                    <RiEyeLine className="h-4 w-4" />
                  </Button>
                </Tooltip>
              </PermissionGuard>
              <PermissionGuard permission="Lead.assignOwner">
                <Tooltip content="Assign Owner">
                  <Button isIconOnly size="sm" variant="light" color="primary" onPress={() => openModal(lead)}>
                    <RiUserLine className="h-4 w-4" />
                  </Button>
                </Tooltip>
              </PermissionGuard>
            </div>
          );
        default:
          return null;
      }
    },
    [navigate, openModal, openAssignSelf, handleExternalLinkClick]
  );

  const [leadToClearOwner, setLeadToClearOwner] = React.useState<Lead | null>(null);
  const [isClearOwnerConfirmOpen, setIsClearOwnerConfirmOpen] = React.useState(false);

  const handleConfirmClearOwner = React.useCallback(async () => {
    if (!leadToClearOwner) return;
    try {
      await clearOwner({ leadId: leadToClearOwner.id, ownerId: null });
      toast.success('Owner removed successfully');
    } catch (error) {
      console.error('Failed to remove owner', error);
    } finally {
      setIsClearOwnerConfirmOpen(false);
      setLeadToClearOwner(null);
    }
  }, [clearOwner, leadToClearOwner]);

  const renderCollapseContent = React.useCallback(
    (lead: Lead) => {
      return (
        <div className="mb-4 space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-4">
              {/* Description Section */}
              {lead.description && (
                <div className="bg-content1 rounded-lg p-4">
                  <h4 className="mb-3 text-sm font-semibold">Description</h4>
                  <p className="text-default-700 text-sm whitespace-pre-wrap">{lead.description}</p>
                </div>
              )}
              {/* Actions */}
              <div className="bg-content1 rounded-lg p-4">
                <div className="mb-3 flex items-center justify-between">
                  <h4 className="text-sm font-semibold">Lead Actions</h4>
                  <div className="flex items-center gap-2">
                    {!lead.owner && (
                      <PermissionGuard permission="Lead.assignSelf">
                        <Button size="sm" color="primary" onPress={() => openAssignSelf(lead)}>
                          <span className="text-white">Assign to me</span>
                        </Button>
                      </PermissionGuard>
                    )}
                    {lead.owner && (
                      <PermissionGuard permission="Lead.assignOwner">
                        <Button
                          size="sm"
                          color="danger"
                          variant="flat"
                          onPress={() => {
                            setLeadToClearOwner(lead);
                            setIsClearOwnerConfirmOpen(true);
                          }}
                        >
                          Remove owner
                        </Button>
                      </PermissionGuard>
                    )}
                  </div>
                </div>
                <div className="divide-default-100 divide-y text-sm">
                  <div className="flex items-center justify-between gap-3 py-2">
                    <span className="text-default-600">Status</span>
                    <PermissionGuard permission="Lead.update">
                      <Select
                        size="sm"
                        selectedKeys={new Set([lead.status])}
                        onSelectionChange={(keys) => {
                          const newStatus = Array.from(keys)[0] as string;
                          if (newStatus && newStatus !== lead.status) {
                            handleStatusChange(lead.id, newStatus as any);
                          }
                        }}
                        isDisabled={isUpdating}
                        className="max-w-[160px]"
                      >
                        <SelectItem key="new">New</SelectItem>
                        <SelectItem key="contacted">Contacted</SelectItem>
                        <SelectItem key="followup">Followup</SelectItem>
                        <SelectItem key="closed">Closed</SelectItem>
                        <SelectItem key="dispensable">Dispensable</SelectItem>
                      </Select>
                    </PermissionGuard>
                  </div>

                  <div className="flex items-center justify-between gap-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="text-default-600 inline-flex items-center gap-1">
                        <RiStickyNoteLine className="h-3.5 w-3.5" />
                        Notes
                      </span>
                      <span className="font-medium">{lead.notes?.length || 0}</span>
                    </div>
                    <PermissionGuard permission="LeadNote.store">
                      <Button
                        size="sm"
                        variant="light"
                        color="warning"
                        startContent={<RiAddLine className="h-4 w-4" />}
                        onPress={() => openAddNoteModal(lead)}
                      >
                        Add Note
                      </Button>
                    </PermissionGuard>
                  </div>

                  <div className="flex items-center justify-between gap-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="text-default-600 inline-flex items-center gap-1">
                        <RiContactsLine className="h-3.5 w-3.5" />
                        Contact Info
                      </span>
                      <span className="font-medium">{lead.contacts?.length || 0}</span>
                    </div>
                    <PermissionGuard permission="LeadContact.store">
                      <Button
                        size="sm"
                        variant="light"
                        color="danger"
                        startContent={<RiAddLine className="h-4 w-4" />}
                        onPress={() => openAddContactModal(lead)}
                      >
                        Add Contact
                      </Button>
                    </PermissionGuard>
                  </div>

                  <div className="flex items-center justify-between gap-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="text-default-600 inline-flex items-center gap-1">
                        <RiChat3Line className="h-3.5 w-3.5" />
                        Interactions
                      </span>
                      <span className="font-medium">{lead.interactions?.length || 0}</span>
                    </div>
                    <PermissionGuard permission="LeadInteraction.store">
                      <Button
                        size="sm"
                        variant="light"
                        startContent={<RiAddLine className="h-4 w-4" />}
                        onPress={() => openAddInteractionModal(lead)}
                      >
                        Add Interaction
                      </Button>
                    </PermissionGuard>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="bg-content1 rounded-lg p-4">
              <h4 className="mb-3 text-sm font-semibold">Additional Information</h4>
              <div className="border-default-100 overflow-hidden rounded-md border text-sm">
                <table className="w-full table-auto text-left">
                  <tbody>
                    {[
                      { label: 'Fetch Mode', value: lead.fetch_mode || '-' },
                      { label: 'Render Status', value: lead.render_status || '-' },
                      { label: 'JS Required', value: lead.js_required ? 'Yes' : 'No' },
                      { label: 'Script Match', value: lead.has_script_match ? 'Yes' : 'No' },
                      { label: 'E-commerce', value: lead.is_ecommerce ? 'Yes' : 'No' },
                      {
                        label: 'E-commerce Platform',
                        value: lead.ecommerce_platform
                          ? `${lead.ecommerce_platform.name} (${(lead.ecommerce_platform.score * 100).toFixed(0)}%)`
                          : '-'
                      },
                      { label: 'Competitors', value: lead.competitor_count },
                      {
                        label: 'Created By',
                        value: lead.created_by ? `${lead.created_by.first_name} ${lead.created_by.last_name}` : '-'
                      },
                      {
                        label: 'Created At',
                        value: lead.created_at ? <DateTimeDisplay dateString={lead.created_at} /> : '-'
                      },
                      {
                        label: 'Updated By',
                        value: lead.updated_by ? `${lead.updated_by.first_name} ${lead.updated_by.last_name}` : '-'
                      },
                      {
                        label: 'Updated At',
                        value: lead.updated_at ? <DateTimeDisplay dateString={lead.updated_at} /> : '-'
                      }
                    ].map((item) => (
                      <tr key={item.label} className="border-default-100 border-t">
                        <td className="text-default-500 px-3 py-2">{item.label}</td>
                        <td className="text-default-900 px-3 py-2 text-right font-medium">{item.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      );
    },
    [openModal, handleStatusChange, isUpdating, openAssignSelf, openAddContactModal, clearOwner, navigate]
  );

  const columns = React.useMemo(
    () => [
      { name: 'Domain', uid: 'domain', sortable: true },
      { name: 'Owner', uid: 'owner', sortable: true },
      { name: 'Score', uid: 'score', sortable: true },
      { name: 'Competitors', uid: 'competitor_count', align: 'center', headerAlign: 'center', sortable: true },
      { name: 'E-commerce', uid: 'ecommerce', sortable: true },
      { name: 'Status', uid: 'status', sortable: true },
      { name: 'Collected At', uid: 'collected_at', sortable: true },
      { name: 'Updated At', uid: 'updated_at', sortable: true },
      { name: 'Actions', uid: 'actions', align: 'end', cellClass: 'text-right' }
    ],
    []
  );

  // Columns visibility management
  const { visibleColumns, setVisibleColumns, headerColumns } = useVisibleColumns(columns);

  const handleSortChange = React.useCallback(
    (descriptor: SortDescriptor) => {
      setSortDescriptor(descriptor);
      if (!descriptor.column) {
        setSortParam(null);
        return;
      }
      let column = descriptor.column.toString();
      // Map frontend column names to backend sort field names
      if (column === 'ecommerce') {
        column = 'is_ecommerce';
      }
      const prefix = descriptor.direction === 'descending' ? '-' : '';
      setSortParam(`${prefix}${column}`);
    },
    [setSortParam]
  );

  // Export hook
  const visibleColumnsSet = React.useMemo(
    () => new Set(Array.from(visibleColumns).map((c) => c.toString())),
    [visibleColumns]
  );

  const leadsMeta = data?.meta;

  const {
    exportState,
    isExporting,
    needsConfirmation,
    totalRecords,
    handleRequestExport,
    handleConfirmExport,
    handleCancelExport
  } = useTableExport<Lead>({
    resourceName: 'Leads',
    apiPath: '/leads',
    params: {
      page: params.page,
      per_page: params.per_page,
      filters: params.filters,
      sort: sortParam || undefined
    },
    meta: leadsMeta
      ? {
          total: leadsMeta.total,
          per_page: leadsMeta.per_page,
          current_page: leadsMeta.current_page,
          last_page: leadsMeta.last_page
        }
      : undefined,
    columns: columns.map((c) => ({ name: c.name, uid: c.uid })),
    visibleColumns: visibleColumnsSet,
    hasAnyFilter,
    mapItemToRow: (lead) => ({
      domain: lead.domain,
      owner: lead.owner ? `${lead.owner.first_name} ${lead.owner.last_name}` : '',
      score: lead.score,
      competitor_count: lead.competitor_count,
      ecommerce: lead.is_ecommerce ? 'Yes' : 'No',
      status: lead.status,
      collected_at: lead.collected_at,
      updated_at: lead.updated_at
    })
  });

  return (
    <TablePage
      columns={columns}
      currentPage={data?.meta?.current_page}
      data={leads}
      emptyMessage="No leads found"
      error={error}
      errorMessage="Failed to load leads"
      enableCollapse={true}
      renderCollapseContent={renderCollapseContent}
      filtersSection={
        <TableFilters
          hasActiveFilters={hasAnyFilter}
          isRefreshing={isLoading}
          onClearFilters={clearFilters}
          onRefresh={triggerRefresh}
          rightSection={
            <div className="flex gap-2">
              <Button
                color="primary"
                size="sm"
                variant="flat"
                onPress={handleOpenAllDomains}
                isDisabled={isLoading || leads.filter((lead) => lead.status === 'new').length === 0}
                startContent={<RiExternalLinkLine className="h-4 w-4" />}
              >
                Open all domains
              </Button>
              <Button
                color="success"
                size="sm"
                variant="flat"
                onPress={handleRequestExport}
                isLoading={isExporting}
                isDisabled={isLoading || !data?.meta?.total}
                startContent={<RiDownloadLine className="h-4 w-4" />}
              >
                Export CSV
              </Button>
            </div>
          }
        >
          <SearchInput
            placeholder="Search domain..."
            value={localDomainSearch}
            onValueChange={setLocalDomainSearch}
            onSearch={handleDomainSearch}
            onClear={handleClearDomainSearch}
          />

          <ColumnsToggleDropdown
            allColumns={columns.map((c) => ({ uid: c.uid, name: c.name }))}
            selected={visibleColumns}
            onChange={(next) => setVisibleColumns(next)}
          />

          <AdvancedFiltersPanel
            statusValue={currentStatus}
            onStatusChange={handleStatusFilterChange}
            scoreValue={currentScore}
            onScoreChange={handleScoreFilterChange}
            ownerValue={currentOwner}
            onOwnerChange={handleOwnerFilterChange}
            ecommerceValue={currentEcommerce}
            onEcommerceChange={handleEcommerceFilterChange}
            competitorsValue={currentCompetitors}
            onCompetitorsChange={handleCompetitorsFilterChange}
            shopValue={currentShop}
            onShopChange={handleShopFilterChange}
            collectedDateValue={currentCollectedDate}
            onCollectedDateChange={handleCollectedDateChange}
            syncedDateValue={currentSyncedDate}
            onSyncedDateChange={handleSyncedDateChange}
            createdDateValue={currentCreatedDate}
            onCreatedDateChange={handleCreatedDateChange}
            updatedDateValue={currentUpdatedDate}
            onUpdatedDateChange={handleUpdatedDateChange}
            activeFiltersCount={activeFiltersCount}
          />
        </TableFilters>
      }
      headerColumns={headerColumns}
      isError={isError}
      isLoading={isLoading}
      renderCell={renderCell}
      sortDescriptor={sortDescriptor}
      title="Leads"
      totalPages={data?.meta?.last_page}
      onPageChange={handlePageChange}
      onRefresh={triggerRefresh}
      onSortChange={handleSortChange}
    >
      <ConfirmDeleteModal
        isOpen={isClearOwnerConfirmOpen}
        onOpenChange={setIsClearOwnerConfirmOpen}
        title="Remove Owner"
        message="Are you sure you want to remove the owner from this lead?"
        confirmText="Remove"
        isLoading={false}
        onConfirm={handleConfirmClearOwner}
      />
      {needsConfirmation && (
        <ExportConfirmModal
          isOpen={exportState === 'confirm'}
          onConfirm={handleConfirmExport}
          onCancel={handleCancelExport}
          totalRecords={totalRecords}
          resourceLabel="lead"
        />
      )}
      <AssignOwnerModal
        isOpen={isModalOpen}
        onOpenChange={onModalOpenChange}
        lead={selectedLead}
        isSubmitting={isSubmitting}
        onAssign={handleAssign}
      />
      <AssignSelfConfirmModal
        isOpen={isAssignSelfOpen}
        onOpenChange={onAssignSelfOpenChange}
        lead={selectedLeadForSelf}
        isSubmitting={isAssigningSelf}
        onConfirm={handleAssignSelf}
      />
      <AddNoteModal
        isOpen={isAddNoteModalOpen}
        onOpenChange={onAddNoteModalOpenChange}
        lead={selectedLeadForNote}
        isSubmitting={isSubmittingNote}
        onSubmit={(note) => handleCreateNote(note)}
      />
      <AddInteractionModal
        isOpen={isAddInteractionModalOpen}
        onOpenChange={onAddInteractionModalOpenChange}
        lead={selectedLeadForInteraction}
        isSubmitting={isSubmittingInteraction}
        onSubmit={(type, note, outcome) => handleCreateInteraction(type, note, outcome)}
      />
      <AddContactModal
        isOpen={isAddContactModalOpen}
        onOpenChange={onAddContactModalOpenChange}
        lead={selectedLeadForContact}
        isSubmitting={isSubmittingContact}
        onSubmit={(type, value, source) => handleCreateContact(type, value, source)}
      />
    </TablePage>
  );
}
